import {
  PERMISSION_ACTION_FUND,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ENTITY_USER,
  ROUTE_CUSTOMERS,
  ROUTE_RECEIVABLES,
  ROUTE_TRANSFERS,
  ROUTE_COLLECTIONS,
  ROUTE_DISBURSEMENTS,
  ROUTE_SUPPLIERS,
  ROUTE_USER,
  ROUTE_SPV_UTILIZATION,
  ROUTE_DOCUMENTS,
  ROUTE_DOCUMENT_REVIEW,
  PERMISSION_ENTITY_DOCUMENTS,
  ROUTE_FUNDER_PRICING,
  ROUTE_REPORTS
} from '@klear/constants'
import { PERMISSION_ENTITY_TRADER, ROUTE_TRADER } from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { getTraderBaseUrl } from '@klear/runtime-env'
import { MoveUpRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useRef } from 'react'
import { Link, useLocation } from 'react-router'

/* prettier-ignore */
const LINK_CLS =
  'block py-2 rounded hover:bg-gray-700 whitespace-nowrap ' +
  'px-0 text-center ' +
  'lg:px-4 lg:text-left ' +
  'lg:group-data-[collapsed]/sidebar:px-0 lg:group-data-[collapsed]/sidebar:text-center'

const Sidebar = () => {
  const { user } = useAuth()
  const location = useLocation()
  const asideRef = useRef<HTMLElement>(null)

  const toggle = () => {
    const el = asideRef.current
    if (!el) return
    el.toggleAttribute('data-collapsed')
  }

  const tabs: { label: string; to: string }[] = []
  if (user.hasKlearRole && user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_VIEW)) {
    tabs.push({ label: 'Receivables', to: `/${ROUTE_RECEIVABLES}` })
  }
  if (
    user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_FUND) ||
    user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_VIEW)
  ) {
    tabs.push({
      label: 'Transfers',
      to: `/${ROUTE_TRANSFERS}`
    })
    tabs.push({
      label: 'Collections',
      to: `/${ROUTE_COLLECTIONS}`
    })
    tabs.push({
      label: 'Disbursements',
      to: `/${ROUTE_DISBURSEMENTS}`
    })
  }
  if (user.hasPermission(PERMISSION_ENTITY_TRADER, PERMISSION_ACTION_VIEW)) {
    tabs.push({ label: 'Traders', to: `/${ROUTE_TRADER}` })
    tabs.push({ label: 'Customers', to: `/${ROUTE_CUSTOMERS}` })
    tabs.push({ label: 'Suppliers', to: `/${ROUTE_SUPPLIERS}` })
  }
  if (user.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_VIEW)) {
    tabs.push({ label: 'Users', to: `/${ROUTE_USER}` })
  }

  const isActive = (to: string) => location.pathname === to || location.pathname.startsWith(to + '/')

  return (
    <aside
      ref={asideRef}
      className="group/sidebar bg-gray-800 text-white h-[calc(100vh-44px)] flex flex-col sticky top-[44px] transition-[width] duration-200 w-12 lg:w-64 lg:data-[collapsed]:w-12"
    >
      <div
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') toggle()
        }}
        className="flex items-center w-full h-10 hover:bg-gray-700 shrink-0 cursor-pointer border-b border-gray-700 justify-center lg:justify-end lg:px-3 lg:group-data-[collapsed]/sidebar:justify-center lg:group-data-[collapsed]/sidebar:px-0"
        aria-label="Toggle sidebar"
      >
        <PanelLeftOpen className="h-4 w-4 block lg:hidden lg:group-data-[collapsed]/sidebar:block" />
        <PanelLeftClose className="h-4 w-4 hidden lg:block lg:group-data-[collapsed]/sidebar:hidden" />
      </div>

      <nav className="space-y-2 overflow-y-auto overflow-x-hidden flex-1">
        {tabs.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            title={tab.label}
            className={`${LINK_CLS} ${isActive(tab.to) ? 'bg-gray-700' : ''}`}
          >
            <span className="inline lg:hidden lg:group-data-[collapsed]/sidebar:inline">{tab.label[0]}</span>
            <span className="hidden lg:inline lg:group-data-[collapsed]/sidebar:hidden">{tab.label}</span>
          </Link>
        ))}
      </nav>

      <div className="pt-4 mt-auto space-y-2 overflow-x-hidden">
        {user.hasKlearRole && (
          <SidebarLink
            to={`/${ROUTE_FUNDER_PRICING}`}
            label="Funder Pricing"
            active={isActive(`/${ROUTE_FUNDER_PRICING}`)}
          />
        )}
        {user.hasPermission(PERMISSION_ENTITY_DOCUMENTS, PERMISSION_ACTION_VIEW) && (
          <SidebarLink
            to={`/${ROUTE_DOCUMENTS}`}
            label="Documents"
            active={isActive(`/${ROUTE_DOCUMENTS}`) || isActive(`/${ROUTE_DOCUMENT_REVIEW}`)}
          />
        )}
        {user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_VIEW) && (
          <SidebarLink
            to={`/${ROUTE_SPV_UTILIZATION}`}
            label="SPV Utilization"
            active={isActive(`/${ROUTE_SPV_UTILIZATION}`)}
          />
        )}
        <SidebarLink to={`/${ROUTE_REPORTS}`} label="Reports" active={isActive(`/${ROUTE_REPORTS}`)} />
        <a
          href={`${getTraderBaseUrl()}/`}
          title="Klear Trader app"
          className="block py-2 rounded hover:bg-gray-700 whitespace-nowrap px-0 text-center lg:px-4 lg:text-left lg:flex lg:items-center lg:justify-between lg:group-data-[collapsed]/sidebar:px-0 lg:group-data-[collapsed]/sidebar:text-center lg:group-data-[collapsed]/sidebar:block"
        >
          <span className="inline lg:hidden lg:group-data-[collapsed]/sidebar:inline">K</span>
          <span className="hidden lg:inline lg:group-data-[collapsed]/sidebar:hidden">Klear Trader app</span>
          <MoveUpRight className="mr-2 h-4 w-4 hidden lg:inline-block lg:group-data-[collapsed]/sidebar:hidden" />
        </a>
      </div>
    </aside>
  )
}

function SidebarLink({ to, label, active }: { to: string; label: string; active: boolean }) {
  return (
    <Link to={to} title={label} className={`${LINK_CLS} ${active ? 'bg-gray-700' : ''}`}>
      <span className="inline lg:hidden lg:group-data-[collapsed]/sidebar:inline">{label[0]}</span>
      <span className="hidden lg:inline lg:group-data-[collapsed]/sidebar:hidden">{label}</span>
    </Link>
  )
}

export default Sidebar
